import {lazy, Suspense} from 'react'
import {isNil} from 'lodash-es'
import type {DatePickerProps as ReactDatePickerProps} from 'react-datepicker'
import {InlineSpinner} from 'elements/InlineSpinner'
import {isoStringToDate, dateToIsoString} from 'helpers/date'
import 'react-datepicker/dist/react-datepicker.css'
import type {FormFieldBaseProps} from 'types/form'
import type {PropsWithClassName} from 'types/util'
import {cnLegacy} from 'utils'
import styles from './DatePicker.module.css'

const ReactDatePicker = lazy(() => import('react-datepicker'))

const getValue = (propValue: unknown): Date | null =>
  isNil(propValue) ? null : isoStringToDate(propValue)

const setValue = (newValue: unknown): string | null =>
  isNil(newValue) ? null : dateToIsoString(newValue)

interface DatePickerProps
  extends PropsWithClassName,
    FormFieldBaseProps<string | null>,
    Pick<ReactDatePickerProps, 'isClearable' | 'fixedHeight'> {
  minDate?: string
  maxDate?: string
  dateFormat?: string
  placeholder?: string
}

export function DatePicker({
  value,
  onChange,
  minDate,
  maxDate,
  placeholder,
  className,
  disabled,
  dateFormat = 'do MMM yyyy',
  ...rest
}: DatePickerProps) {
  return (
    <div
      className={cnLegacy(
        styles.root,
        {
          [styles.disabled]: disabled,
        },
        className
      )}
    >
      <Suspense fallback={<InlineSpinner />}>
        <ReactDatePicker
          {...rest}
          selected={getValue(value)}
          onChange={(newValue) => onChange(setValue(newValue))}
          minDate={getValue(minDate) ?? undefined}
          maxDate={getValue(maxDate) ?? undefined}
          placeholderText={placeholder}
          disabled={disabled}
          className={styles.input}
          dateFormat={dateFormat}
        />
      </Suspense>
    </div>
  )
}
